import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, Globe, LogOut, User, BookOpen, LayoutDashboard, ShieldCheck, GraduationCap, Heart, Award, FileText, Plane, ChevronDown, Layers } from 'lucide-react';
import Logo from './Logo';
import NotificationBell from './cabin/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import useCabinEnrolled from '../hooks/cabin/useCabinEnrolled';
import { localeUpperCase } from '../lib/textCase';
import useReducedMotion from '../hooks/useReducedMotion';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, lang, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [coursesDropOpen, setCoursesDropOpen] = useState(false);

  // Landing sayfasına özel koyu (lacivert/altın) varyant — DİĞER TÜM
  // sayfalarda mevcut açık (light) görünüm birebir korunur. Navbar global
  // bileşen olduğu için bu karar üst bileşenlere dokunmadan kendi içinde
  // veriliyor (en düşük blast radius).
  const isDark = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!isDark) return undefined;
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isDark]);

  // DÜZELTME (landing-v2-duzeltmeler madde 1): header rengi sabitti, hero'nun
  // 3 farklı slayt temasıyla (lacivert/yeşil/bordo) uyumsuzdu — özellikle
  // yeşil slaytta bariz. Artık hero üzerindeyken (henüz scroll edilmemiş
  // landing) header TAMAMEN SAYDAM, altındaki slayt rengi arkasından geçiyor.
  // Scroll edilince (scrolled=true) katı koyu bara geri dönülüyor. SADECE
  // landing'de (isDark) aktif — diğer sayfalarda `duo-navbar` hiç değişmiyor.
  const heroOverlay = isDark && !scrolled;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isInstructor = user?.role === 'instructor' || isAdmin;
  // Danışman rolü öğrenci değil ama instructor/admin de değil — kendi ayrı
  // panosu (/admin/cockpit) var. Navbar'daki PANEL/KURSLAR ve diğer
  // öğrenciye-özel linkler (favoriler/sertifikalar/belgeler/kabin) bu rol
  // için gösterilmiyor; sadece logo, dil, bildirim ve profil/çıkış kalıyor.
  // (Route seviyesinde erişim engellenmedi — bilinçli, bu turun kapsamı
  // sadece navigasyon görünürlüğü.)
  const isDanisman = user?.role === 'danisman';
  const isStudent = !!user && !isInstructor && !isDanisman;

  // Kabin Memurluğu menü görünürlüğü:
  //   - Admin/eğitmen: her zaman göster (kabin yönetim panelleri var)
  //   - Student: course_cabin_main_v1 enrollment teyit edilince göster
  //   - Enrollment yüklenirken/belirsizken gizli (flicker yok)
  //   - Giriş yoksa zaten user===null → link görünmüyor
  //
  // Paylaşılan hook: hem Navbar hem CabinEnrollmentGuard aynı
  // queryKey=['enrollments','cabin-access-check'] kullanır → tek API isteği.
  const { cabinEnrolled, cabinCheckLoading } = useCabinEnrolled(isStudent);
  // isInstructor zaten isAdmin'i kapsıyor
  const showCabinLink = isInstructor || (isStudent && !!cabinEnrolled && !cabinCheckLoading);

  // Koyu zeminde odak halkası görünürlüğü (WCAG 2.4.7) — açık varyantta
  // tarayıcı varsayılanı yeterli, sadece isDark'ta ekstra kontrastlı ring.
  const focusRingDark = isDark ? ' focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C9A227]' : '';

  // navLinkBase artık CSS `uppercase` KULLANMIYOR — Türkçe metinler (örn.
  // "Kabin Memurluğu") locale-aware olmayan CSS-transform ile "KABIN
  // MEMURLUGU" gibi hatalı büyütülüyordu. Büyütme artık her kullanım
  // noktasında localeUpperCase() ile render anında yapılıyor.
  const navLinkBase = "px-3 py-2 rounded-xl text-sm font-bold tracking-wider transition-colors" + focusRingDark;
  const navLinkActive = isDark ? "bg-white/10 text-[#F7F5F0]" : "bg-[var(--duo-sky)] text-[var(--duo-blue)]";
  const navLinkIdle = isDark ? "text-[#b9c4d4] hover:text-[#F7F5F0] hover:bg-white/10" : "text-[var(--duo-text-soft)] hover:text-[var(--duo-text)] hover:bg-[#F7F7F7]";

  // NOT: isDark'ta artık `sticky` DEĞİL `fixed` — landing hero header'ın
  // ALTINDA değil, ÜZERİNDE (overlay) konumlanıyor; bu yüzden hero kendi üst
  // padding'iyle header yüksekliğini telafi ediyor (bkz. HeroSlider.jsx).
  // Diğer sayfalarda (isDark=false) `duo-navbar` (sticky, açık tema) HİÇ
  // değişmiyor — regresyon riski yok.
  const transitionClass = reduced ? '' : ' transition-[background-color,backdrop-filter] duration-300';
  const navClassName = isDark
    ? `fixed top-0 inset-x-0 z-50${transitionClass} ${
        heroOverlay
          ? 'bg-transparent border-b border-transparent'
          : 'bg-[#0B1A33]/95 backdrop-blur-md border-b border-white/10'
      }`
    : 'duo-navbar';

  return (
    <nav className={navClassName} aria-label={t('nav.mainNav')} data-testid="navbar">
      {/* Hero üzerinde okunabilirlik için hafif üst scrim — WCAG kontrastı
          zaten 3 hero temasında da (lacivert/yeşil/bordo) yeterli ölçüldü
          (bkz. rapor), bu sadece ek görsel derinlik/güvenlik payı. Scroll
          sonrası katı bara geçince kaybolur. */}
      {isDark && (
        <div
          className={`absolute inset-x-0 top-0 h-[140px] pointer-events-none${reduced ? '' : ' transition-opacity duration-300'} ${heroOverlay ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,.35), transparent)' }}
          aria-hidden="true"
        />
      )}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo — min-w-0 prevents flex squeezing the wordmark on narrow screens */}
          <Link to="/" className={'flex items-center min-w-0' + focusRingDark} aria-label={t('nav.homeLink')} data-testid="navbar-logo">
            <Logo size={36} wordmarkClassName="hidden sm:inline-flex" wordmarkColor={isDark ? '#F7F5F0' : undefined} />
          </Link>

          {/* Mid nav (auth-only, desktop) */}
          <div className="hidden md:flex items-center gap-1">
            {user && (
              <>
                {!isDanisman && (
                  <Link to="/dashboard" className={navLinkBase + ' ' + (isActive('/dashboard') ? navLinkActive : navLinkIdle)} data-testid="nav-dashboard">
                    {localeUpperCase(t('dashboard'), lang)}
                  </Link>
                )}
                {/* Kurslar dropdown — danışman rolünde gösterilmiyor */}
                {!isDanisman && (
                  <div className="relative" data-testid="nav-courses-dropdown">
                    <button
                      onClick={() => setCoursesDropOpen(v => !v)}
                      onBlur={() => setTimeout(() => setCoursesDropOpen(false), 150)}
                      className={navLinkBase + ' inline-flex items-center gap-1 ' + (location.pathname.startsWith('/courses') || location.pathname === '/my-courses' ? navLinkActive : navLinkIdle)}
                      aria-expanded={coursesDropOpen}
                      data-testid="nav-courses"
                    >
                      {localeUpperCase(t('nav.courses'), lang)}
                      <ChevronDown
                        size={14}
                        strokeWidth={2.5}
                        className={'transition-transform duration-200 ' + (coursesDropOpen ? 'rotate-180' : '')}
                      />
                    </button>
                    {coursesDropOpen && (
                      <div className="absolute top-full left-0 mt-1 w-44 bg-white border-2 border-[var(--duo-stroke)] rounded-xl shadow-lg p-1.5 z-50">
                        <Link
                          to="/my-courses"
                          onClick={() => setCoursesDropOpen(false)}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold text-[var(--duo-text)] hover:bg-[var(--duo-sky)] hover:text-[var(--duo-blue)] transition-colors"
                          data-testid="nav-my-courses"
                        >
                          <BookOpen size={16} strokeWidth={2.5} aria-hidden="true" />
                          {t('nav.myCourses')}
                        </Link>
                        <Link
                          to="/courses"
                          onClick={() => setCoursesDropOpen(false)}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold text-[var(--duo-text)] hover:bg-[var(--duo-sky)] hover:text-[var(--duo-blue)] transition-colors"
                          data-testid="nav-all-courses"
                        >
                          <Layers size={16} strokeWidth={2.5} aria-hidden="true" />
                          {t('nav.allCourses')}
                        </Link>
                      </div>
                    )}
                  </div>
                )}
                {showCabinLink && (
                  <Link
                    to="/programs/kabin-memurlugu/dashboard"
                    className={navLinkBase + ' inline-flex items-center gap-1 ' + (isActive('/programs/kabin-memurlugu') ? navLinkActive : navLinkIdle)}
                    data-testid="nav-cabin-memurlugu"
                  >
                    <Plane size={14} aria-hidden="true" />
                    {localeUpperCase(t('nav.cabinMemurlugu'), lang)}
                  </Link>
                )}
                {isInstructor && (
                  <Link to="/instructor" className={navLinkBase + ' inline-flex items-center gap-1 ' + (isActive('/instructor') ? 'bg-[var(--duo-sand)] text-[var(--duo-orange-dark)]' : navLinkIdle)} data-testid="nav-instructor">
                    <GraduationCap size={14} />
                    {localeUpperCase(lang === 'tr' ? 'Eğitmen' : 'Instructor', lang)}
                  </Link>
                )}
                {isAdmin && (
                  <Link to="/admin" className={navLinkBase + ' inline-flex items-center gap-1 ' + (isActive('/admin') ? 'bg-red-50 text-red-500' : navLinkIdle)} data-testid="nav-admin">
                    <ShieldCheck size={14} />
                    {localeUpperCase('Admin', lang)}
                  </Link>
                )}
              </>
            )}
          </div>

          {/* Right section — shrink-0 prevents flex squeeze on narrow viewports */}
          <div className="flex items-center gap-2.5 shrink-0">
            <button
              onClick={toggleLanguage}
              className={
                'hidden md:flex items-center gap-1 px-2.5 py-1.5 rounded-full border-2 transition-colors' + focusRingDark + ' ' +
                (isDark
                  ? 'border-white/25 text-[#F7F5F0] hover:border-white/40'
                  : 'border-[var(--duo-stroke)] hover:border-[var(--duo-stroke-dark)] text-[var(--duo-text-soft)]')
              }
              data-testid="language-toggle-btn"
              aria-label={t('nav.langToggleLabel')}
            >
              <Globe size={14} strokeWidth={2.5} />
              <span className="text-xs font-bold uppercase tracking-wider">{lang}</span>
            </button>

            {user && (
              <NotificationBell variant="desktop" isDark={isDark} />
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={'flex items-center gap-2 p-1 rounded-full transition-colors' + focusRingDark + ' ' + (isDark ? 'hover:bg-white/10' : 'hover:bg-[#F7F7F7]')} data-testid="user-menu-btn">
                    <div className="w-9 h-9 rounded-full bg-[var(--duo-blue)] flex items-center justify-center overflow-hidden" style={{ boxShadow: '0 3px 0 0 var(--duo-blue-dark)' }}>
                      {user.picture
                        ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
                        : <span className="text-white text-sm font-bold">{user.name?.[0] || 'U'}</span>}
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-bold text-sm">{user.name}</p>
                    <p className="text-xs text-[var(--duo-text-soft)]">{user.email}</p>
                    <span className={'inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ' + (user.role === 'admin' ? 'bg-red-100 text-red-600' : user.role === 'instructor' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600')}>{user.role}</span>
                  </div>
                  <DropdownMenuSeparator />
                  {!isDanisman && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                        <LayoutDashboard size={16} className="mr-2" /> {t('dashboard')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="menu-favorites">
                        <Heart size={16} className="mr-2" /> {lang === 'tr' ? 'Favoriler' : 'Favorites'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/certificates')} data-testid="menu-certificates">
                        <Award size={16} className="mr-2" /> {lang === 'tr' ? 'Sertifikalar' : 'Certificates'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/documents')} data-testid="menu-documents">
                        <FileText size={16} className="mr-2" /> {lang === 'tr' ? 'Belgelerim' : 'My Documents'}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <User size={16} className="mr-2" /> {t('profile')}
                  </DropdownMenuItem>
                  {isInstructor && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/instructor')} data-testid="menu-instructor">
                        <GraduationCap size={16} className="mr-2" /> {lang === 'tr' ? 'Eğitmen Paneli' : 'Instructor Panel'}
                      </DropdownMenuItem>
                    </>
                  )}
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin-dashboard">
                        <ShieldCheck size={16} className="mr-2" /> Admin Dashboard
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/courses')} data-testid="menu-admin-courses">
                        <BookOpen size={16} className="mr-2" /> {lang === 'tr' ? 'Kurslar' : 'Courses'}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-500" data-testid="menu-logout">
                    <LogOut size={16} className="mr-2" /> {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                {/*
                  GİRİŞ YAP: mobilde de görünür, tek CTA.
                  Self-register kapatildi (SELF_REGISTER_AKTIF=false) — eskiden
                  burada ayrica /register'a giden vurgulu "BAŞLA" butonu vardi.
                  O buton /login'e cevirip metnini de "Giris Yap" yapmak, bu
                  ghost link ile ayni hedefe giden birebir yinelenen (ve ayni
                  metinli) iki buton yaratirdi — bu yuzden kaldirildi, tek ve
                  net bir giris CTA'si birakildi.
                */}
                <Link
                  to="/login"
                  className={
                    'inline-flex items-center btn-duo-ghost cta-locale-upper !text-xs !px-3 min-h-[44px] md:min-h-0 md:py-2' + focusRingDark +
                    (isDark ? ' !text-[#F7F5F0] hover:!bg-white/10' : '')
                  }
                  data-testid="nav-login"
                >
                  {localeUpperCase(t('navDuo.ctaLogin'), lang)}
                </Link>
              </div>
            )}

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={
                'md:hidden p-2 rounded-xl min-h-[44px] min-w-[44px] transition-colors' + focusRingDark + ' ' +
                (isDark ? 'text-[#F7F5F0] hover:bg-white/10' : 'hover:bg-[#F7F7F7]')
              }
              data-testid="mobile-menu-btn"
              aria-expanded={mobileOpen}
              aria-controls={mobileOpen ? "mobile-menu" : undefined}
              aria-label={mobileOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div
          id="mobile-menu"
          aria-label={t('nav.mobileMenuLabel')}
          className={
            'md:hidden p-4 space-y-1.5 ' +
            (isDark ? 'bg-[#0d213f] border-t border-white/10' : 'bg-white border-t-2 border-[var(--duo-stroke)]')
          }
          data-testid="mobile-menu"
        >
          {!user && (
            // Self-register kapatildi — mobil menude de tek "Giris Yap" linki yeterli.
            <Link to="/login" onClick={() => setMobileOpen(false)} className={'flex items-center px-4 py-3.5 min-h-[44px] rounded-xl font-bold' + (isDark ? ' text-[#F7F5F0] hover:bg-white/5' : ' text-[var(--duo-text)] hover:bg-[#F7F7F7]')}>{t('navDuo.ctaLogin')}</Link>
          )}
          {user && !isDanisman && <Link to="/dashboard" onClick={() => setMobileOpen(false)} className={'flex items-center px-4 py-3.5 min-h-[44px] rounded-xl font-bold' + (isDark ? ' text-[#F7F5F0] hover:bg-white/5' : ' hover:bg-[#F7F7F7]')}>{t('dashboard')}</Link>}
          {user && !isDanisman && <Link to="/my-courses" onClick={() => setMobileOpen(false)} className={'flex items-center gap-2 px-4 py-3.5 min-h-[44px] rounded-xl font-bold' + (isDark ? ' text-[#F7F5F0] hover:bg-white/5' : ' hover:bg-[#F7F7F7]')}>
            <BookOpen size={16} aria-hidden="true" />{t('nav.myCourses')}
          </Link>}
          {!isDanisman && (
            <Link to="/courses" onClick={() => setMobileOpen(false)} className={'flex items-center gap-2 px-4 py-3.5 min-h-[44px] rounded-xl font-bold' + (isDark ? ' text-[#F7F5F0] hover:bg-white/5' : ' hover:bg-[#F7F7F7]')}>
              <Layers size={16} aria-hidden="true" />{t('nav.allCourses')}
            </Link>
          )}
          {showCabinLink && (
            <Link
              to="/programs/kabin-memurlugu/dashboard"
              onClick={() => setMobileOpen(false)}
              className={'flex items-center gap-2 px-4 py-3.5 min-h-[44px] rounded-xl font-bold' + (isDark ? ' text-[#F7F5F0] hover:bg-white/5' : ' hover:bg-[#F7F7F7] text-[var(--duo-blue)]')}
              data-testid="mobile-nav-cabin-memurlugu"
            >
              <Plane size={16} aria-hidden="true" />
              {t('nav.cabinMemurlugu')}
            </Link>
          )}
          {user && !isDanisman && <Link to="/favorites" onClick={() => setMobileOpen(false)} className={'flex items-center px-4 py-3.5 min-h-[44px] rounded-xl font-bold' + (isDark ? ' text-[#F7F5F0] hover:bg-white/5' : ' hover:bg-[#F7F7F7]')}>{lang === 'tr' ? 'Favoriler' : 'Favorites'}</Link>}
          {user && !isDanisman && <Link to="/certificates" onClick={() => setMobileOpen(false)} className={'flex items-center px-4 py-3.5 min-h-[44px] rounded-xl font-bold' + (isDark ? ' text-[#F7F5F0] hover:bg-white/5' : ' hover:bg-[#F7F7F7]')}>{lang === 'tr' ? 'Sertifikalar' : 'Certificates'}</Link>}
          {user && !isDanisman && <Link to="/documents" onClick={() => setMobileOpen(false)} className={'flex items-center px-4 py-3.5 min-h-[44px] rounded-xl font-bold' + (isDark ? ' text-[#F7F5F0] hover:bg-white/5' : ' hover:bg-[#F7F7F7]')}>{lang === 'tr' ? 'Belgelerim' : 'My Documents'}</Link>}
          {isInstructor && <Link to="/instructor" onClick={() => setMobileOpen(false)} className={'flex items-center px-4 py-3.5 min-h-[44px] rounded-xl font-bold' + (isDark ? ' text-[#F7F5F0] hover:bg-white/5' : ' hover:bg-[#F7F7F7] text-[var(--duo-blue)]')}>{lang === 'tr' ? 'Eğitmen Paneli' : 'Instructor'}</Link>}
          {isAdmin && <Link to="/admin" onClick={() => setMobileOpen(false)} className={'flex items-center px-4 py-3.5 min-h-[44px] rounded-xl font-bold text-red-500' + (isDark ? ' hover:bg-white/5' : ' hover:bg-[#F7F7F7]')}>Admin</Link>}
          {/* Dil değiştirici — üst bardan taşındı, her iki durumda da (guest/logged-in) görünür */}
          <button
            onClick={() => { toggleLanguage(); setMobileOpen(false); }}
            className={'flex items-center gap-2 px-4 py-3.5 min-h-[44px] rounded-xl font-bold w-full text-left' + (isDark ? ' text-[#F7F5F0] hover:bg-white/5' : ' text-[var(--duo-text)] hover:bg-[#F7F7F7]')}
            aria-label={lang === 'tr' ? 'Switch to English' : "Türkçe'ye geç"}
            data-testid="mobile-lang-toggle-btn"
          >
            <Globe size={18} aria-hidden="true" />
            {lang === 'tr' ? 'English' : 'Türkçe'}
          </button>
        </div>
      )}
    </nav>
  );
}
